import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, DropdownButton, MenuItem } from 'react-bootstrap';
import LoaderButton from '../components/LoaderButton';
import {
  authorizeCompanyCam,
  removeCCCapProject,
  getUserIntegrations,
  getCaps,
  disconnectUserIntegration,
  refreshCCProjects,
} from '../utils/bedrockAPI';
import { getUser } from '../utils/Common';
import MonitorSelectionModal from '../components/MonitorSelectionModal';
import companyCamLogo from '../images/company_cam.png';
import './Integrations.css';

function formatSyncTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function syncStatusLabel(capProject) {
  if (!capProject) return { text: 'Not synced yet', tone: 'muted' };
  const status = capProject.last_sync_status;
  const when = formatSyncTime(capProject.last_synced_at);

  if (status === 'ok') {
    const count = capProject.last_photo_count;
    const countLabel = count === 1 ? '1 photo' : `${count || 0} photos`;
    return {
      text: when ? `Pushed ${countLabel} · ${when}` : `Pushed ${countLabel}`,
      tone: 'ok',
    };
  }
  if (status === 'no_images') {
    return {
      text: when ? `No images · ${when}` : 'No images available',
      tone: 'warn',
    };
  }
  if (status === 'error') {
    return {
      text: when
        ? `Failed · ${when}`
        : (capProject.last_sync_error || 'Sync failed'),
      tone: 'error',
    };
  }
  return { text: 'Not synced yet', tone: 'muted' };
}

export default function Integrations() {
  const [userIntegrations, setUserIntegrations] = useState(null);
  const [newConnection, setNewConnection] = useState(false);
  const [capConnection, setCapConnection] = useState(null);
  const [projectConnection, setProjectConnection] = useState(null);
  const [showMonitorSelectionWindow, setShowMonitorSelectionWindow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshingProjects, setIsRefreshingProjects] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [capsById, setCapsById] = useState(null);
  const [caps, setCaps] = useState(null);
  const componentMounted = useRef(true);
  const refreshedIntegrationId = useRef(null);

  async function fetchUserIntegrations(userId) {
    const tempUserIntegrations = await getUserIntegrations(userId);
    if (componentMounted.current) {
      setUserIntegrations(tempUserIntegrations);
    }
    return tempUserIntegrations;
  }

  useEffect(() => {
    async function loadCaps() {
      const tempCaps = await getCaps();
      if (componentMounted.current) setCaps(tempCaps);
    }
    const user = getUser();
    if (user) {
      loadCaps();
      fetchUserIntegrations(user.userId);
    }
    return () => { componentMounted.current = false; };
  }, []);

  useEffect(() => {
    if (!showMonitorSelectionWindow) {
      fetchUserIntegrations(getUser().userId);
    }
  }, [showMonitorSelectionWindow]);

  useEffect(() => {
    if (caps) {
      const tempCapsById = {};
      for (let i = 0; i < caps.length; i++) {
        tempCapsById[caps[i].capid] = caps[i];
      }
      setCapsById(tempCapsById);
    }
  }, [caps]);

  // Pull fresh CompanyCam projects whenever the page has a connected account
  useEffect(() => {
    async function refreshProjects() {
      const integration = userIntegrations?.[0];
      if (!integration || integration.name !== 'CompanyCam') return;
      if (refreshedIntegrationId.current === integration.userIntegrationId) return;

      refreshedIntegrationId.current = integration.userIntegrationId;
      setIsRefreshingProjects(true);
      setRefreshError(null);
      try {
        const updated = await refreshCCProjects(integration.userIntegrationId);
        if (componentMounted.current && updated) {
          setUserIntegrations([updated]);
        }
      } catch (err) {
        console.log(err);
        if (componentMounted.current) {
          setRefreshError('Could not refresh Company Cam projects. Try reconnecting if this persists.');
          refreshedIntegrationId.current = null;
        }
      } finally {
        if (componentMounted.current) setIsRefreshingProjects(false);
      }
    }
    refreshProjects();
  }, [userIntegrations]);

  const integration = userIntegrations?.[0];
  const isCompanyCamConnected = integration?.name === 'CompanyCam';
  const capProjects = integration?.integration?.cap_projects || {};
  const projects = integration?.integration?.projects || [];
  const hasActiveProjects = projects.length > 0 && !projects.every(p => p.status === 'deleted');
  const capProjectKeys = Object.keys(capProjects);
  const lastDaemonRun = formatSyncTime(integration?.integration?.last_daemon_run_at);

  function findProjectById(projectList, projectId) {
    if (projectList && projectList.length > 0) {
      for (let i = 0; i < projectList.length; i++) {
        if (String(projectList[i].id) === String(projectId)) {
          return projectList[i];
        }
      }
    }
    return null;
  }

  function addUserIntegration(e) {
    e.preventDefault();
    setShowMonitorSelectionWindow(true);
    setNewConnection(false);
  }

  async function deleteCapProject(e, capProject) {
    e.preventDefault();
    if (capProject && integration?.integration) {
      await removeCCCapProject(integration, capProject);
      fetchUserIntegrations(getUser().userId);
    }
  }

  async function handleDisconnect() {
    if (!integration?.userIntegrationId) return;
    const confirmed = window.confirm(
      'Disconnect Company Cam? Cap project mappings and daily photo sync will stop.'
    );
    if (!confirmed) return;

    setIsDisconnecting(true);
    try {
      await disconnectUserIntegration(integration.userIntegrationId);
      refreshedIntegrationId.current = null;
      if (componentMounted.current) {
        setUserIntegrations([]);
        setNewConnection(false);
        setCapConnection(null);
        setProjectConnection(null);
        setRefreshError(null);
      }
    } catch (err) {
      console.log(err);
      window.alert('Unable to disconnect Company Cam. Please try again.');
    } finally {
      if (componentMounted.current) setIsDisconnecting(false);
    }
  }

  async function handleManualRefresh() {
    if (!integration?.userIntegrationId) return;
    refreshedIntegrationId.current = null;
    setIsRefreshingProjects(true);
    setRefreshError(null);
    try {
      const updated = await refreshCCProjects(integration.userIntegrationId);
      refreshedIntegrationId.current = integration.userIntegrationId;
      if (componentMounted.current && updated) {
        setUserIntegrations([updated]);
      }
    } catch (err) {
      console.log(err);
      if (componentMounted.current) {
        setRefreshError('Could not refresh Company Cam projects. Try reconnecting if this persists.');
      }
    } finally {
      if (componentMounted.current) setIsRefreshingProjects(false);
    }
  }

  return (
    <div className="integrations-page">
      <header className="integrations-header">
        <nav className="brand-breadcrumb">
          <Link to="/dashboard">Dashboard</Link>
          <span className="brand-breadcrumb-sep">/</span>
          <Link to="/account/info">My Account</Link>
          <span className="brand-breadcrumb-sep">/</span>
          <span className="brand-breadcrumb-current">Integrations</span>
        </nav>
        <h1 className="integrations-title">Integrations</h1>
        <p className="integrations-subtitle">
          Connect Bedrock camera systems to third-party services.
        </p>
      </header>

      <section className="integrations-panel">
        <div className="integrations-provider">
          <img className="integrations-logo" src={companyCamLogo} alt="Company Cam" />
          <div className="integrations-provider-actions">
            {isCompanyCamConnected ? (
              <>
                <Button className="btn-brand-outline btn-compact integrations-connected-btn" disabled>
                  <i className="fas fa-check-circle" /> Connected
                </Button>
                <LoaderButton
                  className="btn-brand-outline btn-compact"
                  isLoading={isRefreshingProjects}
                  onClick={handleManualRefresh}
                  disabled={isDisconnecting}
                >
                  <i className="fas fa-sync-alt" /> Refresh projects
                </LoaderButton>
                <LoaderButton
                  className="btn-brand-outline btn-compact integrations-btn-danger"
                  isLoading={isDisconnecting}
                  onClick={handleDisconnect}
                  disabled={isRefreshingProjects}
                >
                  Disconnect
                </LoaderButton>
              </>
            ) : (
              <LoaderButton
                className="btn-brand-dark btn-compact"
                isLoading={isLoading}
                href={authorizeCompanyCam()}
              >
                Connect Daily Feed to Company Cam
              </LoaderButton>
            )}
          </div>
        </div>

        {isCompanyCamConnected && (
          <div className="integrations-status-bar">
            {isRefreshingProjects && (
              <span className="integrations-status-muted">Refreshing Company Cam projects…</span>
            )}
            {!isRefreshingProjects && lastDaemonRun && (
              <span className="integrations-status-muted">
                Last daily sync run: {lastDaemonRun}
              </span>
            )}
            {!isRefreshingProjects && !lastDaemonRun && (
              <span className="integrations-status-muted">
                Waiting for the first daily photo sync.
              </span>
            )}
            {refreshError && (
              <span className="integrations-status-error">{refreshError}</span>
            )}
          </div>
        )}

        {isCompanyCamConnected && capProjectKeys.length > 0 && (
          <div className="integrations-connections">
            <h2 className="integrations-section-title">Synced cameras</h2>
            <div className="integrations-table-wrap">
              <table className="integrations-table">
                <thead>
                  <tr>
                    <th>Bedrock CAP</th>
                    <th>Company Cam project</th>
                    <th>Last photo sync</th>
                    <th className="integrations-table-actions-col">Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {capProjectKeys.map((capProject) => {
                    const mapping = capProjects[capProject];
                    const status = syncStatusLabel(mapping);
                    return (
                      <tr key={capProject}>
                        <td>{capsById?.[capProject]?.name || capProject}</td>
                        <td>
                          {findProjectById(projects, mapping.project_id)?.name || '—'}
                        </td>
                        <td>
                          <div className="integrations-sync-cell">
                            {mapping.last_photo_uri && status.tone === 'ok' && (
                              <img
                                className="integrations-sync-thumb"
                                src={mapping.last_photo_uri}
                                alt="Last photo pushed to Company Cam"
                              />
                            )}
                            <span className={`integrations-sync-status integrations-sync-status--${status.tone}`}>
                              {status.text}
                            </span>
                          </div>
                        </td>
                        <td className="integrations-table-actions-col">
                          <Button
                            className="btn-brand-outline btn-compact integrations-btn-danger"
                            onClick={(e) => deleteCapProject(e, capProject)}
                            title="Remove connection"
                          >
                            <i className="fas fa-times" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {newConnection && (
          <div className="integrations-new-connection">
            <DropdownButton
              title={capConnection && capsById?.[capConnection] ? capsById[capConnection].name : 'Select a CAP'}
              id="cap-list"
              className="integrations-dropdown"
              onSelect={(key) => setCapConnection(key)}
            >
              {caps?.filter(cap => cap.status === 'ACTIVE').map(cap => (
                <MenuItem key={cap.capid} eventKey={cap.capid}>{cap.name}</MenuItem>
              ))}
            </DropdownButton>
            <DropdownButton
              title={
                projectConnection
                  ? findProjectById(projects, projectConnection)?.name
                  : 'Select a Company Cam project'
              }
              id="project-list"
              className="integrations-dropdown"
              onSelect={(key) => setProjectConnection(key)}
            >
              {projects
                .filter(project => project.status !== 'deleted')
                .map(project => (
                  <MenuItem key={project.id} eventKey={project.id}>{project.name}</MenuItem>
                ))}
            </DropdownButton>
            <Button
              className="btn-brand-dark btn-compact"
              onClick={addUserIntegration}
              disabled={!capConnection || !projectConnection}
            >
              <i className="fas fa-check" />
            </Button>
          </div>
        )}

        {isCompanyCamConnected && hasActiveProjects && (
          <div className="integrations-add-row">
            <p className="integrations-add-label">Connect a CAP to a Company Cam project</p>
            <Button
              className="btn-brand-outline btn-compact"
              disabled={newConnection || isRefreshingProjects}
              onClick={() => setNewConnection(true)}
            >
              <i className="fas fa-plus" /> Add connection
            </Button>
          </div>
        )}

        {isCompanyCamConnected && !hasActiveProjects && !isRefreshingProjects && (
          <p className="integrations-note">
            To connect a CAP to Company Cam, create a project at{' '}
            <a href="https://app.companycam.com/" target="_blank" rel="noopener noreferrer">
              CompanyCam
            </a>
            , then refresh projects here.
          </p>
        )}
      </section>

      {capConnection && (
        <MonitorSelectionModal
          show={showMonitorSelectionWindow}
          cap={capConnection}
          project={projectConnection}
          integration={integration || null}
          setShowMonitorSelectionWindow={setShowMonitorSelectionWindow}
        />
      )}
    </div>
  );
}
